import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Heart, Trash2, ChefHat } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { SkeletonLoader } from "@/components/system";

type Recipe = {
  id: string;
  title: string;
  source_url: string | null;
  source_type: string | null;
  servings: number;
  prep_minutes: number | null;
  cook_minutes: number | null;
  cuisine: string | null;
  meal_type: string[] | null;
  dietary_tags: string[] | null;
  instructions: string | null;
  notes: string | null;
  rating: number | null;
  favorite: boolean;
  cover_image_url: string | null;
  computed_kcal_per_serving: number | null;
  computed_protein_g: number | null;
  computed_carbs_g: number | null;
  computed_fat_g: number | null;
  cooked_count: number;
};

type Ingredient = {
  id: string;
  line_number: number;
  raw_text: string;
  quantity: number | null;
  unit: string | null;
  notes: string | null;
};

export function RecipeDetail() {
  const { id } = useParams<{ id: string }>();
  const { session } = useSession();
  const userId = session?.user?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const recipe = useQuery({
    queryKey: ["recipe", id, userId],
    enabled: Boolean(userId && id),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("recipe")
        .select("*")
        .eq("id", id!)
        .eq("user_id", userId!)
        .single();
      if (error) throw error;
      return data as Recipe;
    },
  });

  const ingredients = useQuery({
    queryKey: ["recipe_ingredients", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("recipe_ingredient")
        .select("*")
        .eq("recipe_id", id!)
        .order("line_number");
      if (error) throw error;
      return (data ?? []) as Ingredient[];
    },
  });

  const toggleFavorite = useMutation({
    mutationFn: async () => {
      if (!recipe.data) return;
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("recipe")
        .update({ favorite: !recipe.data.favorite })
        .eq("id", recipe.data.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recipe", id] }),
  });

  const del = useMutation({
    mutationFn: async () => {
      if (!confirm("Delete this recipe?")) return;
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("recipe")
        .delete()
        .eq("id", id!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recipes", userId] });
      navigate("/recipes");
    },
  });

  const markCooked = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("recipe")
        .update({
          cooked_count: (recipe.data?.cooked_count ?? 0) + 1,
          last_cooked_at: new Date().toISOString(),
        })
        .eq("id", id!);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recipe", id] }),
  });

  if (recipe.isLoading) return <SkeletonLoader variant="card" height={400} />;
  if (!recipe.data) return <p>Recipe not found.</p>;

  const r = recipe.data;
  const totalMin = (r.prep_minutes ?? 0) + (r.cook_minutes ?? 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 720, margin: "0 auto" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <Link to="/recipes" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Recipes
        </Link>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 32 }}>
          {r.title}
        </h1>
      </header>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => markCooked.mutate()} style={primaryBtn}>
          <ChefHat size={14} /> Mark cooked
        </button>
        <button onClick={() => toggleFavorite.mutate()} style={ghostBtn}>
          <Heart size={14} fill={r.favorite ? "currentColor" : "none"} />
          {r.favorite ? "Favorited" : "Favorite"}
        </button>
        <button onClick={() => del.mutate()} style={{ ...ghostBtn, color: "var(--severity-bad)" }}>
          <Trash2 size={14} /> Delete
        </button>
      </div>

      <article
        style={{
          padding: 16,
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          background: "var(--bg-2)",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 16 }}>
          <Stat label="Servings" value={String(r.servings)} />
          <Stat label="Total time" value={totalMin > 0 ? `${totalMin} min` : "—"} />
          <Stat
            label="Per serving"
            value={r.computed_kcal_per_serving != null ? `${Math.round(r.computed_kcal_per_serving)} kcal` : "—"}
          />
          <Stat
            label="P / C / F"
            value={
              r.computed_protein_g != null
                ? `${Math.round(r.computed_protein_g)} / ${Math.round(r.computed_carbs_g ?? 0)} / ${Math.round(r.computed_fat_g ?? 0)} g`
                : "—"
            }
          />
        </div>
      </article>

      <section>
        <h2 style={{ margin: "0 0 8px 0", fontSize: "var(--type-body)" }}>Ingredients</h2>
        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {(ingredients.data ?? []).map((ing) => (
            <li
              key={ing.id}
              style={{
                padding: "8px 0",
                borderBottom: "1px solid var(--hair)",
                fontSize: "var(--type-body-s)",
              }}
            >
              {ing.raw_text}
            </li>
          ))}
        </ul>
      </section>

      {r.instructions && (
        <section>
          <h2 style={{ margin: "0 0 8px 0", fontSize: "var(--type-body)" }}>Instructions</h2>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              fontSize: "var(--type-body-s)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {r.instructions}
          </pre>
        </section>
      )}

      {r.source_url && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--fg-3)" }}>
          Source: <a href={r.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>{r.source_url}</a>
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 11, color: "var(--fg-3)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {label}
      </span>
      <span className="mono" style={{ fontSize: "var(--type-body)", fontWeight: 500 }}>{value}</span>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-on)",
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const ghostBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--fg)",
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid var(--hair-2)",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};
